/**
 * usePhotoRatingsGraphQL Hooks
 *
 * Apollo Client hooks for photo rating queries and mutations.
 * Feature: 649-critique-feedback-options (T028)
 */

import { useMutation, useQuery, gql } from '@apollo/client';
import logger from '@/utils/logger';

// ============================================================================
// GraphQL Operations
// ============================================================================

/**
 * Get current user's rating for a photo in a session
 */
const MY_RATING_FOR_PHOTO_QUERY = gql`
  query MyRatingForPhoto($photoId: ID!, $sessionId: ID!) {
    myRatingForPhoto(photoId: $photoId, sessionId: $sessionId) {
      id
      globalId
      rating
    }
  }
`;

/**
 * Get rating summary for a photo in a session
 */
const RATING_SUMMARY_FOR_PHOTO_QUERY = gql`
  query RatingSummaryForPhoto($photoId: ID!, $sessionId: ID!) {
    ratingSummaryForPhoto(photoId: $photoId, sessionId: $sessionId) {
      photoId
      averageRating
      voteCount
    }
  }
`;

/**
 * Submit a rating for a photo
 */
const SUBMIT_RATING_MUTATION = gql`
  mutation SubmitRating($input: SubmitRatingInput!) {
    submitRating(input: $input) {
      rating {
        id
        globalId
        photoId
        sessionId
        rating
      }
      created
    }
  }
`;

/**
 * Delete a rating
 */
const DELETE_RATING_MUTATION = gql`
  mutation DeleteRating($input: DeleteRatingInput!) {
    deleteRating(input: $input) {
      success
      message
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface PhotoRating {
  id: string;
  globalId: string;
  rating: number;
}

export interface RatingSummary {
  photoId: string;
  averageRating: number;
  voteCount: number;
}

interface MyRatingQueryResult {
  myRatingForPhoto: PhotoRating | null;
}

interface RatingSummaryQueryResult {
  ratingSummaryForPhoto: RatingSummary | null;
}

interface SubmitRatingResult {
  submitRating: {
    rating: PhotoRating & { photoId: string; sessionId: string };
    created: boolean;
  };
}

interface DeleteRatingResult {
  deleteRating: {
    success: boolean;
    message: string;
  };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to get current user's rating for a photo in a session
 *
 * @param photoId - Global ID of the photo
 * @param sessionId - Global ID of the critique session
 * @param options - Optional configuration (skip to disable query)
 * @returns Query result with myRating
 *
 * @example
 * ```tsx
 * const { myRating, loading } = useMyRatingForPhoto(photoId, sessionId);
 * if (myRating) {
 *   console.log(`User rated: ${myRating.rating} stars`);
 * }
 * ```
 */
export function useMyRatingForPhoto(
  photoId: string | null | undefined,
  sessionId: string | null | undefined,
  options?: { skip?: boolean }
) {
  const skip = options?.skip || !photoId || !sessionId;

  const { data, loading, error, refetch } = useQuery<MyRatingQueryResult>(
    MY_RATING_FOR_PHOTO_QUERY,
    {
      variables: { photoId, sessionId },
      skip,
      fetchPolicy: 'cache-and-network',
    }
  );

  if (error) {
    logger.error('PhotoRatings', 'Failed to fetch user rating', {
      photoId,
      sessionId,
      error: error.message,
    });
  }

  return {
    myRating: data?.myRatingForPhoto ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to get rating summary (average, vote count) for a photo in a session
 *
 * @param photoId - Global ID of the photo
 * @param sessionId - Global ID of the critique session
 * @param options - Optional configuration (skip to disable query)
 * @returns Query result with ratingSummary
 *
 * @example
 * ```tsx
 * const { ratingSummary, loading } = useRatingSummaryForPhoto(photoId, sessionId);
 * if (ratingSummary) {
 *   console.log(`Average: ${ratingSummary.averageRating}, Votes: ${ratingSummary.voteCount}`);
 * }
 * ```
 */
export function useRatingSummaryForPhoto(
  photoId: string | null | undefined,
  sessionId: string | null | undefined,
  options?: { skip?: boolean }
) {
  const skip = options?.skip || !photoId || !sessionId;

  const { data, loading, error, refetch } = useQuery<RatingSummaryQueryResult>(
    RATING_SUMMARY_FOR_PHOTO_QUERY,
    {
      variables: { photoId, sessionId },
      skip,
      fetchPolicy: 'cache-and-network',
    }
  );

  if (error) {
    logger.error('PhotoRatings', 'Failed to fetch rating summary', {
      photoId,
      sessionId,
      error: error.message,
    });
  }

  return {
    ratingSummary: data?.ratingSummaryForPhoto ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook to submit a rating for a photo
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const { submitRating, loading, error } = useSubmitRating();
 *
 * const handleRate = async (rating: number) => {
 *   const result = await submitRating(photoId, sessionId, rating);
 *   if (result?.created) {
 *     console.log('New rating created!');
 *   } else {
 *     console.log('Rating updated!');
 *   }
 * };
 * ```
 */
export function useSubmitRating() {
  const [mutate, { loading, error }] = useMutation<SubmitRatingResult>(
    SUBMIT_RATING_MUTATION
  );

  const submitRating = async (
    photoId: string,
    sessionId: string,
    rating: number
  ) => {
    try {
      const result = await mutate({
        variables: {
          input: { photoId, sessionId, rating },
        },
        refetchQueries: [
          {
            query: MY_RATING_FOR_PHOTO_QUERY,
            variables: { photoId, sessionId },
          },
          {
            query: RATING_SUMMARY_FOR_PHOTO_QUERY,
            variables: { photoId, sessionId },
          },
        ],
      });

      logger.info('PhotoRatings', 'Rating submitted', {
        photoId,
        sessionId,
        rating,
        created: result.data?.submitRating.created,
      });

      return result.data?.submitRating ?? null;
    } catch (err) {
      logger.error('PhotoRatings', 'Failed to submit rating', {
        photoId,
        sessionId,
        rating,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  return {
    submitRating,
    loading,
    error,
  };
}

/**
 * Hook to delete a rating
 *
 * @returns Mutation function and state
 *
 * @example
 * ```tsx
 * const { deleteRating, loading } = useDeleteRating();
 *
 * const handleDelete = async () => {
 *   const result = await deleteRating(ratingId);
 *   if (result?.success) {
 *     console.log('Rating deleted!');
 *   }
 * };
 * ```
 */
export function useDeleteRating() {
  const [mutate, { loading, error }] = useMutation<DeleteRatingResult>(
    DELETE_RATING_MUTATION
  );

  const deleteRating = async (ratingId: string) => {
    try {
      const result = await mutate({
        variables: {
          input: { ratingId },
        },
      });

      logger.info('PhotoRatings', 'Rating deleted', { ratingId });

      return result.data?.deleteRating ?? null;
    } catch (err) {
      logger.error('PhotoRatings', 'Failed to delete rating', {
        ratingId,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  return {
    deleteRating,
    loading,
    error,
  };
}
